/**
 * White Label Configuration
 * 
 * This file defines the theme configuration for different branded versions of the application.
 * Each product (brand) has its own theme with customizable colors and logos.
 * 
 * To add a new brand:
 * 1. Add the product name to the ProductType union
 * 2. Create logo assets in public/assets/images/
 * 3. Add theme configuration to the WHITE_LABEL_THEMES object
 */

export type ProductType = 'atollon' | 'healtek' | 'agriflow';

export interface WhiteLabelTheme {
  /** Background color for the login page */
  backgroundColor: string;
  /** Path to the background image */
  backgroundImage: string;
  /** Path to the main logo (large vertical logo) */
  mainLogo: string;
  /** Path to the title logo (small logo in dialog) */
  titleLogo: string;
  /** Dashboard top logo configuration (displayed at top left corner) */
  dashboardTopLogo: {
    /** Path to the logo image */
    source: string;
    /** Padding to be applied to the logo container */
    padding: string;
  };
  /** Background color for the login dialog */
  dialogBackgroundColor: string;
  /** Primary color for buttons and interactive elements */
  baseColor: string;
  /** Product name for display purposes */
  productName: string;
  /** Website title for HTML meta tags */
  siteTitle: string;
  /** Website description for HTML meta tags */
  siteDescription: string;
  /** Translation key for the welcome message */
  welcomeMessageKey: string;
}

/**
 * White label theme configurations for each supported product
 */
export const WHITE_LABEL_THEMES: Record<ProductType, WhiteLabelTheme> = {
  atollon: {
    backgroundColor: '#EFF7FE',
    backgroundImage: '/assets/images/public-background-web.svg',
    mainLogo: '/assets/images/logo-web.svg',
    titleLogo: '/assets/images/logo-web.svg',
    dashboardTopLogo: {
      source: '/assets/images/atollon-logo.svg',
      padding: '0'
    },
    dialogBackgroundColor: '#FFFFFF',
    baseColor: '#0073E6',
    productName: 'Atollon',
    siteTitle: 'Atollon',
    siteDescription: 'Atollon Reef - CRM, ERP, Project Management, Intranet, Extranet, Web, Mobile, API, Integration, E-commerce, E-shop, E-market',
    welcomeMessageKey: 'welcome'
  },
  healtek: {
    backgroundColor: '#E9EBEC',
    backgroundImage: '/assets/images/healtek-background.svg',
    mainLogo: '/assets/images/healtek-logo-main.svg',
    titleLogo: '/assets/images/healtek-logo-title.svg',
    dashboardTopLogo: {
      source: '/assets/images/healtek-logo-title.svg',
      padding: '20px 30px'
    },
    dialogBackgroundColor: '#FFFFFF',
    baseColor: '#00B0F0',
    productName: 'Healtek',
    siteTitle: 'Healtek',
    siteDescription: 'Healtek - Healthcare Management, Patient Care, Medical Records, Hospital Management, Clinical Operations',
    welcomeMessageKey: 'welcome_healtek'
  },
  agriflow: {
    backgroundColor: '#E9EBEC',
    backgroundImage: '/assets/images/agriflow-background.svg',
    mainLogo: '/assets/images/agriflow-logo-main.svg',
    titleLogo: '/assets/images/agriflow-logo-title.svg',
    dashboardTopLogo: {
      source: '/assets/images/agriflow-logo-title.svg',
      padding: '18px'
    },
    dialogBackgroundColor: '#FFFFFF',
    baseColor: '#00796B',
    productName: 'Agriflow',
    siteTitle: 'Agriflow',
    siteDescription: 'Agriflow - Agricultural Management, Farm Operations, Crop Planning, Supply Chain, Resource Management',
    welcomeMessageKey: 'welcome_agriflow'
  }
};

/**
 * Get theme configuration for a specific product
 * @param product - The product type
 * @returns Theme configuration for the product
 */
export function getWhiteLabelTheme(product: string): WhiteLabelTheme {
  const normalizedProduct = product?.toLowerCase() as ProductType;
  
  if (normalizedProduct && normalizedProduct in WHITE_LABEL_THEMES) {
    return WHITE_LABEL_THEMES[normalizedProduct];
  }
  
  // Default to Atollon theme for unknown products
  console.warn(`Unknown product "${product}", defaulting to Atollon theme`);
  return WHITE_LABEL_THEMES.atollon;
}

/**
 * Check if a product requires white label login page
 * @param product - The product type
 * @returns True if the product should use the white label login page
 */
export function requiresWhiteLabelLogin(product: string): boolean {
  const normalizedProduct = product?.toLowerCase();
  return normalizedProduct === 'healtek' || normalizedProduct === 'agriflow';
}
